import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISavingsTransactionsData } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IGetBucketTransactionsRequest {
  pageNumber?: number;
  pageSize?: number;
  searchParams: {
    savingsAccountNumber: string;
  };
}

interface IGetBucketTransactionsResponse {
  status?: boolean;
  message?: string;
  totalCount?: number;
  data?: ISavingsTransactionsData;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetBucketTransactionsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getBucketTransactionsAsync = createAsyncThunk(
  'bucket-transactions',
  async (
    {
      pageNumber = 1,
      pageSize = 10,
      searchParams,
    }: IGetBucketTransactionsRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<IGetBucketTransactionsResponse>(
        '/savings/Transactions/search',
        { pageSize, pageNumber, searchParams },
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const bucketTransactionsGetSlice = createSlice({
  name: 'bucketTransactions/get',
  initialState,
  reducers: {
    resetGetBucketTransactions: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBucketTransactionsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getBucketTransactionsAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetBucketTransactionsResponse;
      })
      .addCase(getBucketTransactionsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetGetBucketTransactions } =
  bucketTransactionsGetSlice.actions;

export default bucketTransactionsGetSlice.reducer;
